// Modal.tsx

import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styles from './Settings.module.css';
import useOnClickOutside from '../hooks/useOnClickOutside.ts';
import Dropdown from './Dropdown.tsx';
import { toast } from '../utils/sweetAlert.ts';
import { CustomMultiSelect } from './CustomSelect.tsx';
import { useDeleteUser } from '../hooks/useDeleteUser.ts';
import { useCreateUser } from '../hooks/useCreateUser.ts';
import { useEditUser } from '../hooks/useEditUser.ts';
import { useForm } from 'react-hook-form';
import { useCreateOrganization } from '../hooks/useCreateOrganization.ts';
import { useDeleteOrganization } from '../hooks/useDeleteOrganization.ts';
import { useUsers } from '../hooks/useUsers.ts';
import { useAddUserToOrg } from '../hooks/useAddUserToOrg.ts';
import { useRemoveUserFromOrg } from '../hooks/useRemoveUserFromOrg.ts';
import { useOrganizations } from '../hooks/useOrganizations.ts';
import { useEditOrganization } from '../hooks/useEditOrganization.ts';
import { useTheme } from '../hooks/useTheme.ts';

interface User {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  roles: string[];
  organizations: { id: number; name: string }[];
}

interface GraphType {
  value: string;
  label: string;
}

type Props = {
  open?: boolean;
  setOpen?: (state: boolean) => void;
  openClient?: boolean;
  setOpenClient?: (state: boolean) => void;

  editOrg?: boolean;
  orgId?: string;
  confirmOrganization?: boolean;

  edit?: boolean;
  userId?: string;
  confirm?: boolean;

  addUserToOrganization?: boolean;
  confirmOrganizationUser?: boolean;

  refreshToken?: boolean;
  onRefreshToken?: () => void;
  onLogout?: () => void;

  organization?: string;
  allUsers?: User[];
  action?: 'enable' | 'disable';
  onConfirmEnablePad?: () => void;
};

interface OptionType<T> {
  value: T;
  label: string;
}

export type EquationOption = OptionType<number>;
export type ChannelOption = OptionType<string>;

// Utility Function to get Roles
function getRoles(role: string[] | undefined): string {
  if (!role || role.length === 0) {
    return 'No role specified';
  } else {
    const rolesObject: { [key: string]: string } = {
      Admin: 'Admin',
      User: 'User',
      Viewer: 'Viewer',
      Editor: 'Editor',
      'Sensor provider': 'Sensor Provider',
      Manager: 'Manager',
    };
    return rolesObject[role[0]] || role[0];
  }
}

// Utility Function to get Role Array
function getRole(role: string): string[] {
  const roles = [];
  const rolesObject: { [key: string]: string } = {
    Admin: 'Admin',
    User: 'User',
    Viewer: 'Viewer',
    Editor: 'Editor',
    'Sensor provider': 'Sensor provider',
    Manager: 'Manager',
  };
  roles.push(rolesObject[role]);
  return roles;
}

// Modal Component
function Modal({
  setOpen,
  openClient,
  setOpenClient,
  editOrg,
  orgId,
  confirmOrganization,
  edit,
  userId,
  confirm,
  addUserToOrganization,
  confirmOrganizationUser,
  refreshToken,
  onRefreshToken,
  onLogout,
  organization,
  allUsers,
  action,
}: Props): JSX.Element {
  const { theme } = useTheme();
  const isDarkTheme = theme === 'dark';
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const [formData] = useState(() => {
    const user = allUsers?.find((user) => user?.id === userId);
    return {
      name: user?.first_name || 'Not set',
      lastName: user?.last_name || 'Not set',
      roles: getRoles(user?.roles) || 'Not set',
    };
  });

  const { allOrganizations } = useOrganizations();

  const [formDataEdit] = useState(() => {
    if (!orgId) return { nameOrg: '', addressOrg: '', phoneOrg: '', emailOrg: '' };
    const org = allOrganizations?.find((org) => org?.id === Number(orgId));
    return {
      nameOrg: org?.name || '',
      addressOrg: org?.address || '',
      phoneOrg: org?.phone || '',
      emailOrg: org?.email || '',
    };
  });

  const { allUsers: users } = useUsers<User>();
  const { addUserToOrg } = useAddUserToOrg();
  const { removeUser: removeUserFromOrg } = useRemoveUserFromOrg();
  const { isDeleting: isDeletingUser, deleteUser } = useDeleteUser(userId || '');
  const { isDeleting: isDeletingOrg, deleteOrganization } = useDeleteOrganization();

  const { isCreating: isCreatingOrg, createOrganization } = useCreateOrganization();
  const { isCreating: isCreatingUser, createUser } = useCreateUser();
  const { isEditing: isEditingUser, editUser } = useEditUser();
  const { isEditing: isEditingOrg, editOrganization } = useEditOrganization();

  const [templateOptions, setTemplateOptions] = useState<{ value: string; label: string }[] | undefined>([]);
  const [graphTemplates, setGraphTemplates] = useState<GraphType[]>([]);

  const [role, setSelectState] = useState('');

  useEffect(() => {
    if (confirmOrganizationUser) {
      const result = users
        ?.filter((user) => user?.organizations.some((org) => org.name === organization))
        ?.map((item) => ({
          value: item.id,
          label: `${item.first_name} ${item.last_name}`,
        }));
      setTemplateOptions(result);
    }
    if (addUserToOrganization) {
      const result = users
        ?.filter((user) => !user?.organizations.some((org) => org.name === organization))
        ?.map((item) => ({
          value: item.id,
          label: `${item.first_name} ${item.last_name}`,
        }));
      setTemplateOptions(result);
    }
  }, [users, organization, addUserToOrganization, confirmOrganizationUser]);

  const handleSelectGraphChange = (selectedOptions: GraphType[]) => {
    setGraphTemplates(selectedOptions);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    document.body.classList.add('no-scroll');
    return () => {
      document.body.classList.remove('no-scroll');
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [setOpen]);

  function handleClickCancel() {
    toast('error', 'Changes you made may not be saved!');
    setOpen(false);
  }

  function handleCancelAddOrganization() {
    if (setOpenClient) setOpenClient(false);
  }

  function handleCancelEditOrg() {
    setOpen(false);
  }

  function handleCancelAddUser() {
    setOpen(false);
  }

  async function handleConfirmDeleteOrganization() {
    if (orgId) {
      await deleteOrganization(orgId);
    }
    setOpen(false);
  }

  async function handleConfirmDelete() {
    if (userId) {
      await deleteUser(userId);
    }
    setOpen(false);
  }

  const modalRef = useRef<HTMLDivElement>(null);
  const addRef = useRef<HTMLDivElement>(null);

  const clickOutSideHandler = () => {
    setOpen(false);
  };

  useOnClickOutside(modalRef, clickOutSideHandler);
  useOnClickOutside(addRef, clickOutSideHandler);

  const onSubmitEdit = (data: any) => {
    const updatedUser = {
      firstName: data.firstName,
      lastName: data.lastName,
      userId: userId || 'Not set',
      roles: getRole(role),
    };
    editUser(updatedUser);
    setOpen(false);
  };

  const onSubmitAddUserToOrganization = () => {
    if (orgId) {
      const payload = {
        userIds: (graphTemplates as GraphType[]).map((item) => item.value),
        orgId: orgId,
      };
      addUserToOrg(payload);
    }
    setOpen(false);
  };

  const onSubmitRemoveUserFromOrganization = () => {
    if (orgId) {
      const payload = {
        userIds: (graphTemplates as GraphType[]).map((item) => item.value),
        orgId: orgId,
      };
      removeUserFromOrg(payload);
    }
    setOpen(false);
  };

  const onSubmit = async (data: any) => {
    const newUser = {
      firstName: data.addFirstName,
      lastName: data.addLastName,
      email: data.addEmail,
      roles: getRole(role),
    };
    await createUser(newUser);
    setOpen(false);
  };

  const onSubmitOrganization = async (data: any) => {
    const trimmedInputValue = data.name?.trim();
    if (!trimmedInputValue) {
      toast('error', 'The organization name cannot be empty.');
      return;
    }
    const isDuplicate = allOrganizations?.some((org) => org?.name === data.name) || false;
    if (!isDuplicate) {
      const newOrg = {
        name: data.name,
        address: data.address,
        email: data.email,
        phone: data.phone,
      };
      await createOrganization(newOrg);
      setOpenClient(false);
    } else {
      toast('error', 'Organization already exists');
    }
  };

  const onSubmitEditOrganization = async (data: any) => {
    const trimmedInputValue = data.name?.trim();
    if (!trimmedInputValue) {
      toast('error', 'The organization name cannot be empty.');
      return;
    }
    if (!orgId) {
      toast('error', 'Organization ID is missing.');
      return;
    }
    const updatedOrg = {
      name: data.name,
      address: data.address,
      phone: data.phone,
      email: data.email,
      orgId: orgId,
    };
    await editOrganization(updatedOrg);
    setOpen(false);
  };

  if (refreshToken) {
    return (
      <section className={`${styles.overlay}`}>
        <div className={`${styles.modal} ${isDarkTheme ? 'dark-theme' : ''}`} ref={modalRef}>
          <p className={styles.title_modal}>Session Expiring Soon</p>
          <p className={styles.paragraph_modal}>
            Your session is about to expire. Would you like to refresh your token to stay logged in?
          </p>
          <div className={styles.buttons}>
            <button
              className={styles.cancel_button}
              type="button"
              onClick={onLogout}
              style={{
                backgroundColor: isDarkTheme ? '#525252' : '',
                color: isDarkTheme ? '#ffffff' : '',
                border: isDarkTheme ? '1px solid #737373' : '',
                fontWeight: 600,
                opacity: 1,
                visibility: 'visible',
              }}
            >
              Log Out
            </button>
            <button
              className={styles.modal_add_user}
              type="button"
              onClick={onRefreshToken}
            >
              Refresh Token
            </button>
          </div>
        </div>
      </section>
    );
  }

  if (confirmOrganizationUser) {
    return (
      <section className={`${styles.overlay}`}>
        <div className={`${styles.modal} ${isDarkTheme ? 'dark-theme' : ''}`} ref={modalRef}>
          <p className={styles.title_modal}>Remove User from Organization</p>
          <form onSubmit={handleSubmit(onSubmitRemoveUserFromOrganization)}>
            <CustomMultiSelect
              placeholder="Search users..."
              options={templateOptions ?? []}
              selected={graphTemplates}
              onChange={handleSelectGraphChange}
            />
            <div className={styles.buttons}>
              <button type="button" className={styles.cancel_button} onClick={handleClickCancel}>
                Cancel
              </button>
              <button
                className={styles.modal_add_user}
                type="submit"
                disabled={(graphTemplates as GraphType[]).length === 0}
              >
                Remove
              </button>
            </div>
          </form>
        </div>
      </section>
    );
  }

  if (confirmOrganization) {
    return (
      <section className={`${styles.overlay}`}>
        <div className={`${styles.modal} ${isDarkTheme ? 'dark-theme' : ''}`} ref={modalRef}>
          <p className={styles.title_modal}>Confirm Organization Deletion</p>
          <p className={styles.paragraph_modal}>Are you sure you want to delete this organization? This action cannot be undone.</p>
          <div className={styles.buttons}>
            <button className={styles.cancel_button} type="button" onClick={() => setOpen(false)}>
              Cancel
            </button>
            <button
              className={styles.modal_add_user}
              onClick={handleConfirmDeleteOrganization}
              disabled={isDeletingOrg}
            >
              {isDeletingOrg ? 'Deleting...' : 'Proceed'}
            </button>
          </div>
        </div>
      </section>
    );
  }

  if (confirm) {
    return (
      <section className={`${styles.overlay}`}>
        <div className={`${styles.modal} ${isDarkTheme ? 'dark-theme' : ''}`} ref={modalRef}>
          <p className={styles.title_modal}>Confirm User Deletion</p>
          <p style={{ color: isDarkTheme ? '#ffffff' : 'inherit' }}>
            Are you sure you want to delete{' '}
            <strong>
              {formData.name} {formData.lastName}
            </strong>
            's account? This action cannot be undone.
          </p>
          <div className={styles.buttons}>
            <button 
              className={styles.cancel_button} 
              type="button" 
              onClick={() => setOpen(false)}
              style={{ 
                backgroundColor: isDarkTheme ? '#525252' : '', 
                color: isDarkTheme ? '#ffffff' : '',
                border: isDarkTheme ? '1px solid #737373' : '',
                fontWeight: 600,
                opacity: 1,
                visibility: 'visible'
              }}
            >
              Cancel
            </button>
            <button
              className={styles.modal_add_user}
              onClick={handleConfirmDelete}
              disabled={isDeletingUser}
            >
              {isDeletingUser ? 'Deleting...' : 'Proceed'}
            </button>
          </div>
        </div>
      </section>
    );
  }

  if (edit) {
    return (
      <section className={`${styles.overlay}`}>
        <div className={`${styles.modal} ${isDarkTheme ? 'dark-theme' : ''}`} ref={modalRef}>
          <p className={styles.title_modal}>Edit User</p>
          <form onSubmit={handleSubmit(onSubmitEdit)}>
            <label className={styles.label} htmlFor="firstName">
              First Name
            </label>
            <input
              type="text"
              defaultValue={formData.name}
              className={errors?.firstName ? styles.error_input : styles.input}
              placeholder="Enter first name"
              id="firstName"
              {...register('firstName', { required: 'This field is required' })}
            />
            {errors?.firstName && (
              <p className={styles.error}>{errors.firstName.message.toString()}</p>
            )}

            <label className={styles.label} htmlFor="lastName">
              Last Name
            </label>
            <input
              type="text"
              className={errors?.lastName ? styles.error_input : styles.input}
              defaultValue={formData.lastName}
              placeholder="Enter last name"
              id="lastName"
              {...register('lastName', { required: 'This field is required' })}
            />
            {errors?.lastName && (
              <p className={styles.error}>{errors.lastName.message.toString()}</p>
            )}

            <label className={styles.label} htmlFor="role">
              Role
            </label>
            <div>
              <Dropdown title={formData.roles} maxLength={true} setSelectState={setSelectState} />
            </div>

            <div className={styles.buttons}>
              <button type="button" className={styles.cancel_button} onClick={handleClickCancel}>
                Cancel
              </button>
              <button
                className={styles.modal_add_user}
                type="submit"
                disabled={isEditingUser}
              >
                {isEditingUser ? 'Editing...' : 'Edit'}
              </button>
            </div>
          </form>
        </div>
      </section>
    );
  }

  if (addUserToOrganization) {
    return (
      <section className={`${styles.overlay}`}>
        <div className={`${styles.modal} ${isDarkTheme ? 'dark-theme' : ''}`} ref={modalRef}>
          <p className={styles.title_modal}>Add User to Organization</p>
          <form onSubmit={handleSubmit(onSubmitAddUserToOrganization)}>
            <CustomMultiSelect
              placeholder="Search users..."
              options={templateOptions ?? []}
              selected={graphTemplates}
              onChange={handleSelectGraphChange}
            />
            <div className={styles.buttons}>
              <button type="button" className={styles.cancel_button} onClick={handleClickCancel}>
                Cancel
              </button>
              <button
                className={styles.modal_add_user}
                type="submit"
                disabled={(graphTemplates as GraphType[]).length === 0}
              >
                Add
              </button>
            </div>
          </form>
        </div>
      </section>
    );
  }

  if (openClient) {
    return ReactDOM.createPortal(
      <section className={`${styles.overlay}`}>
        <div className={`${styles.modal} ${isDarkTheme ? 'dark-theme' : ''}`} ref={addRef}>
          <p className={styles.title_modal}>Add Organization</p>
          <form onSubmit={handleSubmit(onSubmitOrganization)}>
            <label className={styles.label} htmlFor="name">
              Organization Name
            </label>
            <input
              autoComplete="off"
              type="text"
              className={errors?.name ? styles.error_input : styles.input}
              placeholder="Enter organization name"
              id="name"
              {...register('name', { required: 'This field is required' })}
            />
            {errors?.name && <p className={styles.error}>{errors.name.message.toString()}</p>}

            <label className={styles.label} htmlFor="address">
              Address
            </label>
            <input
              autoComplete="off"
              type="text"
              className={errors?.address ? styles.error_input : styles.input}
              placeholder="Enter address"
              id="address"
              {...register('address')}
            />
            {errors?.address && <p className={styles.error}>{errors.address.message.toString()}</p>}

            <label className={styles.label} htmlFor="phone">
              Phone Number
            </label>
            <input
              autoComplete="off"
              type="text"
              className={errors?.phone ? styles.error_input : styles.input}
              placeholder="Enter phone number"
              id="phone"
              {...register('phone')}
            />
            {errors?.phone && <p className={styles.error}>{errors.phone.message.toString()}</p>}

            <label className={styles.label} htmlFor="email">
              Email
            </label>
            <input
              autoComplete="off"
              type="email"
              className={errors?.email ? styles.error_input : styles.input}
              placeholder="Enter email"
              id="email"
              {...register('email', {
                pattern: {
                  value: /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/,
                  message: 'Invalid email. Please enter a valid email address.',
                },
              })}
            />
            {errors?.email && <p className={styles.error}>{errors.email.message.toString()}</p>}

            <div className={styles.buttons}>
              <button
                className={styles.cancel_button}
                type="button"
                onClick={handleCancelAddOrganization}
              >
                Cancel
              </button>
              <button
                className={styles.modal_add_user}
                type="submit"
                disabled={isCreatingOrg}
              >
                {isCreatingOrg ? 'Adding...' : 'Add'}
              </button>
            </div>
          </form>
        </div>
      </section>,
      document.body,
    );
  }

  if (editOrg) {
    return ReactDOM.createPortal(
      <section className={`${styles.overlay}`}>
        <div className={`${styles.modal} ${isDarkTheme ? 'dark-theme' : ''}`} ref={addRef}>
          <p className={styles.title_modal}>Edit Organization</p>
          <form onSubmit={handleSubmit(onSubmitEditOrganization)}>
            <label className={styles.label} htmlFor="name">
              Organization Name
            </label>
            <input
              autoComplete="off"
              defaultValue={formDataEdit.nameOrg}
              type="text"
              className={errors?.name ? styles.error_input : styles.input}
              placeholder="Enter organization name"
              id="name"
              {...register('name', { required: 'This field is required' })}
            />
            {errors?.name && (
              <p className={styles.error}>{errors.name.message.toString()}</p>
            )}

            <label className={styles.label} htmlFor="address">
              Address
            </label>
            <input
              autoComplete="off"
              defaultValue={formDataEdit.addressOrg}
              type="text"
              className={errors?.address ? styles.error_input : styles.input}
              placeholder="Enter address"
              id="address"
              {...register('address')}
            />
            {errors?.address && (
              <p className={styles.error}>{errors.address.message.toString()}</p>
            )}

            <label className={styles.label} htmlFor="phone">
              Phone Number
            </label>
            <input
              autoComplete="off"
              defaultValue={formDataEdit.phoneOrg}
              type="text"
              className={errors?.phone ? styles.error_input : styles.input}
              placeholder="Enter phone number"
              id="phone"
              {...register('phone')}
            />
            {errors?.phone && (
              <p className={styles.error}>{errors.phone.message.toString()}</p>
            )}

            <label className={styles.label} htmlFor="email">
              Email
            </label>
            <input
              autoComplete="off"
              defaultValue={formDataEdit.emailOrg}
              type="email"
              className={errors?.email ? styles.error_input : styles.input}
              placeholder="Enter email"
              id="email"
              {...register('email', {
                pattern: {
                  value: /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/,
                  message: 'Invalid email. Please enter a valid email address.',
                },
              })}
            />
            {errors?.email && (
              <p className={styles.error}>{errors.email.message.toString()}</p>
            )}

            <div className={styles.buttons}>
              <button className={styles.cancel_button} type="button" onClick={handleCancelEditOrg}>
                Cancel
              </button>
              <button
                className={styles.modal_add_user}
                type="submit"
                disabled={isEditingOrg}
              >
                {isEditingOrg ? 'Editing...' : 'Edit'}
              </button>
            </div>
          </form>
        </div>
      </section>,
      document.body,
    );
  }

  return (
    <section className={`${styles.overlay}`}>
      <div className={`${styles.modal} ${isDarkTheme ? 'dark-theme' : ''}`} ref={addRef}>
        <p className={styles.title_modal}>Add User</p>
        <form onSubmit={handleSubmit(onSubmit)}>
          <label className={styles.label} htmlFor="addFirstName">
            First Name
          </label>
          <input
            type="text"
            className={errors?.addFirstName ? styles.error_input : styles.input}
            placeholder="Enter first name"
            id="addFirstName"
            {...register('addFirstName', { required: 'This field is required' })}
          />
          {errors?.addFirstName && (
            <p className={styles.error}>{errors.addFirstName.message.toString()}</p>
          )}

          <label className={styles.label} htmlFor="addLastName">
            Last Name
          </label>
          <input
            type="text"
            className={errors?.addLastName ? styles.error_input : styles.input}
            placeholder="Enter last name"
            id="addLastName"
            {...register('addLastName', { required: 'This field is required' })}
          />
          {errors?.addLastName && (
            <p className={styles.error}>{errors.addLastName.message.toString()}</p>
          )}

          <label className={styles.label} htmlFor="addEmail">
            Email
          </label>
          <input
            type="email"
            className={errors?.addEmail ? styles.error_input : styles.input}
            placeholder="Enter email"
            id="addEmail"
            {...register('addEmail', {
              required: 'This field is required',
              validate: {
                matchPattern: (v) =>
                  /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(v) ||
                  'Invalid email. Please enter your email in the format: name@example.com',
              },
            })}
          />
          {errors?.addEmail && (
            <p className={styles.error}>{errors.addEmail.message.toString()}</p>
          )}

          <label className={styles.label} htmlFor="role">
            Role
          </label>
          <div>
            <Dropdown title={'Select role'} maxLength={true} setSelectState={setSelectState} />
          </div>

          <div className={styles.buttons}>
            <button 
              className={styles.cancel_button} 
              type="button" 
              onClick={handleCancelAddUser}
              style={{ 
                backgroundColor: isDarkTheme ? '#525252' : '', 
                color: isDarkTheme ? '#ffffff' : '',
                border: isDarkTheme ? '1px solid #737373' : '',
                fontWeight: 600,
                opacity: 1,
                visibility: 'visible'
              }}
            >
              Cancel
            </button>
            <button
              className={styles.modal_add_user}
              type="submit"
              disabled={isCreatingUser}
            >
              {isCreatingUser ? 'Adding...' : 'Add'}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

export default Modal;
